<template>
  <div>
    <b-table :items="items" v-if="items.length > 0" :fields="fields">

      <template v-slot:cell(reference)="row">
        <b-form-group v-if="items[row.index].isEdit">
          <b-input-group>
            <b-form-input v-model="items[row.index].reference" type="text"></b-form-input>
          </b-input-group>
        </b-form-group>
        <div v-else>
          {{row.value}}
        </div>
      </template>

      <template v-slot:cell(courier)="row">
        <user-autocomplete v-if="items[row.index].isEdit" ref="companyAc" @selected="setFleetRentalOrderCourier($event, row.index)" :includes="['country']" />
        <div v-if="row.item.courier">
          <user-card :user='row.item.courier' :profile-picture="false" :with-titles="false" :vehicle-types-prop="vehicleTypes" />
          <b-button v-if="items[row.index].isEdit" size="sm" variant="danger" @click="deleteFleetRentalOrderCourier(row.index)" class="mt-2 p-1 px-2"><font-awesome-icon icon="trash-alt" /></b-button>
        </div>
        <strong v-else>
          <translate>No Courier set</translate>
        </strong>
      </template>

      <template v-slot:cell(vehicle)="row">
        <vehicle-autocomplete v-if="items[row.index].isEdit" ref="vehicleAc" @selected="setFleetRentalOrderVehicle($event, row.index)" />
        <div v-if="row.item.vehicle" class="position-relative">
          <div>
            <h5 class="mb-0">
              #{{row.item.vehicle.id}}
              {{row.item.vehicle.reference}}
              <small v-if="row.item.vehicle.vehicle_type && row.item.vehicle.vehicle_type.name"><em>({{ row.item.vehicle.vehicle_type.name }})</em></small>
              <b-badge variant="dark" v-if="row.item.vehicle.plate_number">
                {{row.item.vehicle.plate_number}}
              </b-badge>
            </h5>
          </div>
          <b-button v-if="items[row.index].isEdit" size="sm" variant="danger" @click="deleteFleetRentalOrderVehicle(row.index)" class="mt-2 p-1 px-2"><font-awesome-icon icon="trash-alt" /></b-button>
        </div>
        <strong v-else>
          <translate>No Vehicle set</translate>
        </strong>
      </template>

      <template v-slot:cell(commission)="row">
        <b-form-group v-if="items[row.index].isEdit">
          <b-input-group>
            <b-form-input v-model="items[row.index].commission" type="number" step="0.01" required></b-form-input>
            <template #append>
              <b-input-group-text>{{items[row.index].currency || '-'}}</b-input-group-text>
            </template>
          </b-input-group>
          <b-form-text v-translate="{price: items[row.index].price || parseInt(fleetRentalOrder.agreed_price / items.length), currency: items[row.index].currency || ''}">Estimated client price: %{price} %{currency}</b-form-text>
          <b-form-text v-translate="{price: row.item.meta.serviceDetail.quantity * fleetRentalOrder.meta.serviceDefinition.unitPrice * (fleetRentalOrder.meta.serviceDefinition.commissionPercentage || 60) / 100, currency: items[row.index].currency || ''}">Estimated courier commission: %{price} %{currency}</b-form-text>

          <b-button size="sm" class="p-0 px-1" @click.prevent="selectedRow = row" v-b-modal.modalEditServiceItems>
            <small>
              <font-awesome-icon icon="edit" />
              <span v-translate="{qty: row.item.meta.serviceDetail.quantity}">%{qty} Services</span>
            </small>
          </b-button>
        </b-form-group>
        <div v-else>
          {{row.value | currency(row.item.currency)}}<br />
          <b-button size="sm" class="p-0 px-1" variant="dark" :id="'tooltip-target-' + row.index">
            <small>
              <font-awesome-icon :icon="['far', 'eye']" />
              <span v-translate="{qty: row.item.meta.serviceDetail.quantity}">%{qty} Services</span>
            </small>
          </b-button>
          <b-tooltip :target="'tooltip-target-' + row.index" triggers="hover">
            <div style="min-width: 250px" class="text-left">
              <h6>{{ $parent.unitTypes.find(x => x.value === fleetRentalOrder.meta.serviceDefinition.unitType).text || $gettext('Generic') }}</h6>
              <div v-if="fleetRentalOrder.meta.serviceDefinition.unitType.indexOf('weight') === -1">
                <ol v-if="row.item.meta.serviceDetail.items.length > 0">
                  <li v-for="(item, i) in (row.item.meta.serviceDetail.items || [])" :key="i">
                    {{ item || '-' }}
                  </li>
                </ol>
                <p class="mb-0" v-else>{{ $gettext('None assigned') }}</p>
              </div>
              <div v-else>
                <p class="mb-0" v-if="!row.item.meta.serviceDetail.quantity">{{ $gettext('None assigned') }}</p>
                <p class="mb-0" v-else>{{ row.item.meta.serviceDetail.quantity }}</p>
              </div>

            </div>
          </b-tooltip>
        </div>
      </template>

      <template #cell(status)="row">
        <b-form-group v-if="items[row.index].isEdit">
          <b-input-group>
            <b-form-select v-model="items[row.index].status" :options="fleetRentalOrderVehicleStatuses" required :disabled="items[row.index].status === 'incidence' && !isFleetAdmin">
              <template #first>
                <option :value="null" v-translate>Select</option>
              </template>
            </b-form-select>
          </b-input-group>
          <b-form-group v-if="items[row.index].status === 'incidence'">
            <label v-translate>Notes</label>
            <b-input-group>
              <b-form-textarea v-model="items[row.index].notes" :state="items[row.index].status === 'incidence' && !(!items[row.index].notes && !items[row.index].status_history[0].notes)" :disabled="items[row.index].status === 'incidence' && !isFleetAdmin"></b-form-textarea>
            </b-input-group>
          </b-form-group>
        </b-form-group>
        <div v-else>
          <div :class="'text-' + fleetRentalOrderVehicleStatuses.find(x => x.value === row.item.status).variant">
            {{ fleetRentalOrderVehicleStatuses.find(x => x.value === row.item.status).text }}
            <b-button v-if="items[row.index].status_history && items[row.index].status_history.length > 0 && items[row.index].status_history[0].notes" :id="'tooltip-comments-' + row.index" size="sm" class="p-0 px-1" variant="dark">
              <font-awesome-icon icon="comment" />
              <b-tooltip :target="'tooltip-comments-' + row.index">
                <div v-html="$options.filters.brize(items[row.index].status_history[0].notes)"></div>
              </b-tooltip>
            </b-button>
          </div>
        </div>
      </template>

      <template #cell(place)="row">
        <div v-if="items[row.index].isEdit">
          <address-autocomplete id="search-origin-address" @selected="setFleetRentalOrderVehiclePlaceId($event, row.index)" static-items :minCharacter="4" class="mb-2" />
        </div>
        <div v-if="items[row.index].place_id" :class="!items[row.index].isEdit ? 'shipping-address-listings' : ''" :title="row.item.place.formatted_address">
          {{row.item.place.formatted_address}}
          <b-badge v-if="items[row.index].isEdit" variant="danger" href="#" @click.prevent="deleteFleetRentalOrderVehiclePlaceId(row.index)">×</b-badge>
        </div>
        <strong v-else>
          <translate>No Address set</translate>
        </strong>
      </template>

      <template v-slot:cell(instructions)="row">
        <b-form-group v-if="items[row.index].isEdit">
          <b-input-group>
            <b-form-textarea v-model="items[row.index].instructions"></b-form-textarea>
          </b-input-group>
        </b-form-group>
        <div v-else v-html="$options.filters.brize(row.value)"></div>
      </template>

      <template #cell(edit)="row">

        <b-button v-if="!items[row.index].isEdit" size="sm" @click="editRowHandler(row)" class="p-1 px-2" :disabled="editing">
          <font-awesome-icon icon="edit" />
        </b-button>
        <div v-else>
          <b-button variant="success" size="sm" @click="editRowHandler(row); saveFleetRentalOrderVehicle(row);" class="p-1 px-2">
            <font-awesome-icon icon="check" />
          </b-button>
          <b-button variant="danger" size="sm" @click="cancelEdit(row)" class="p-1 px-2">
            <strong>✕</strong>
          </b-button>
          <b-button v-if="fleetRentalOrder.status === 'finished'" size="sm" @click="openModalAccountingNote(row.item)" v-translate>Accounting Note</b-button>
        </div>
        <b-dropdown v-if="!items[row.index].isEdit" right variant="danger" size="sm" :disabled="editing">
          <template #button-content>
            <font-awesome-icon icon="trash-alt"  class="p-0" />
          </template>
          <b-dropdown-item-button @click="cleanupRowData(row.index)"><translate>Clean up row data</translate></b-dropdown-item-button>
          <b-dropdown-item-button @click="deleteRow(row)" variant="danger"><translate>Completely DELETE row</translate></b-dropdown-item-button>
        </b-dropdown>
      </template>

    </b-table>

    <b-modal id="modalEditServiceItems" ref="modalEditServiceItems" hide-footer @hidden="selectedRow = {}">
      <div slot="modal-title">
        <translate>Edit Services</translate>
      </div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>

      <b-row>
        <b-col>
          <dl>
            <dt v-translate>Service Type</dt>
            <dd>{{ $parent.unitTypes.find(x => x.value === fleetRentalOrder.meta.serviceDefinition.unitType).text || $gettext('Generic') }}</dd>
          </dl>
        </b-col>
        <b-col>
          <dl>
            <dt v-translate>Service Quantity</dt>
            <dd>{{ fleetRentalOrder.meta.serviceDefinition.quantity }}</dd>
          </dl>
        </b-col>
        <b-col>
          <dl>
            <dt v-translate>Already Assigned</dt>
            <dd>{{ alreadyAssigned }}</dd>
          </dl>
        </b-col>
      </b-row>

      <div v-if="selectedRow && selectedRow.item && fleetRentalOrder.meta.serviceDefinition.unitType.indexOf('weight') === -1">
        <label v-if="selectedRow.item.meta.serviceDetail.items.length > 0" v-translate>Codes/Numbers</label>
        <b-form-group v-for="(item, index) in (selectedRow.item.meta.serviceDetail.items || [])" :key="index">
          <b-input-group>
            <b-form-input v-model="selectedRow.item.meta.serviceDetail.items[index]" type="text"></b-form-input>
            <b-input-group-append>
              <b-button @click="deleteServiceDetailItem(index)" variant="danger">✕</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <form @submit.prevent="addServiceDetailItem">
          <b-form-group>
            <label for="reference" v-translate>Code/Number</label>
            <b-input-group>
              <b-form-input v-model="serviceDetail.code" type="text"></b-form-input>
              <b-input-group-append>
                <b-button :disabled="fleetRentalOrder.meta.serviceDefinition.quantity <= alreadyAssigned" type="submit" v-translate>Add</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </form>
      </div>
      <div v-else>
        <b-form-group v-if="selectedRow.item">
          <b-input-group>
            <b-form-input v-model="selectedRow.item.meta.serviceDetail.quantity" type="number" :formatter="setMinMaxServices"></b-form-input>
            <b-input-group-append>
              <b-input-group-text>{{ $parent.unitTypes.find(x => x.value === fleetRentalOrder.meta.serviceDefinition.unitType).text || $gettext('Generic') }}</b-input-group-text>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="text-center">
        <b-button @click.prevent="$refs.modalEditServiceItems.hide()" v-translate>Done</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import AddressMixins from '@/mixins/address.mixin'
import UserAutocomplete from '@/components/autocompletes/UserAutocomplete'
import AddressAutocomplete from '@/components/autocompletes/AddressAutocomplete'
import VehicleAutocomplete from '@/components/autocompletes/VehicleAutocomplete'

export default {
  name: 'FleetRentalOrderVehicleList',
  components: {
    UserAutocomplete,
    AddressAutocomplete,
    VehicleAutocomplete
  },
  props: {
    fleetRentalOrder: {
      type: Object,
      defualt: () => {}
    },
    fleetRentalOrderVehicles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      editing: false,
      item: {
        reference: null,
        vehicle_id: null,
        vehicle: null,
        courier_id: null,
        courier: null,
        commission: 0,
        instructions: null,
        meta: {
          serviceDetail: {
            items: [],
            quantity: 0
          }
        },
        place_id: null,
        place: null,
        // price: null,
        status: 'pending'
      },
      items: [],
      fields: [
        {
          key: 'reference',
          label: this.$gettext('Reference')
        },
        {
          key: 'courier',
          label: this.$gettext('Courier')
        },
        {
          key: 'vehicle',
          label: this.$gettext('Vehicle')
        },
        {
          key: 'commission',
          label: this.$gettext('Commission')
        },
        {
          key: 'status',
          label: this.$gettext('Status')
        },
        {
          key: 'place',
          label: this.$gettext('Origin Address')
        },
        {
          key: 'instructions',
          label: this.$gettext('Instructions')
        },
        {
          key: 'edit',
          label: ''
        }
      ],
      fleetRentalOrderVehicleStatuses: [
        {
          text: this.$gettext('Pending'),
          value: 'pending',
          variant: 'info'
        },
        {
          text: this.$gettext('Assigned'),
          value: 'assigned',
          variant: 'warning'
        },
        {
          text: this.$gettext('Confirmed'),
          value: 'confirmed',
          variant: 'success'
        },
        {
          text: this.$gettext('Present'),
          value: 'present',
          variant: 'success'
        },
        {
          text: this.$gettext('Incidence'),
          value: 'incidence',
          variant: 'warning'
        },
        {
          text: this.$gettext('Cancelled'),
          value: 'canceled',
          variant: 'danger'
        },
        {
          text: this.$gettext('Done'),
          value: 'done',
          variant: 'success'
        }
      ],
      selectedRow: {},
      serviceDetail: {
        code: ''
      }
    }
  },
  computed: {
    alreadyAssigned() {
      return this.items.reduce((n, {meta}) => { return n + (meta && meta.serviceDetail && meta.serviceDetail.quantity ? parseInt(meta.serviceDetail.quantity) : 0) }, 0)
    }
  },
  watch: {
    fleetRentalOrderVehicles: {
      handler(newValue, oldValue) {
        if (this.fleetRentalOrder.amount_requested > newValue.length) {
          for (let i = 0; i < (this.fleetRentalOrder.amount_requested - newValue.length); i++) {
            newValue.push({...JSON.parse(JSON.stringify(this.item)), currency: this.fleetRentalOrder.currency, fleet_rental_order_id: this.fleetRentalOrder.id})
          }
        }
        newValue = newValue.map(x => {
          if (!x.meta) {
            x.meta = {
              serviceDetail: {
                items: [],
                quantity: 0
              }
            }
          }
          if (!x.meta.serviceDetail) {
            x.meta.serviceDetail = {
              items: [],
              quantity: 0
            }
          }
          return x
        })
        this.items = JSON.parse(JSON.stringify(newValue.map(x => {
          if (x.status_history && x.status_history.length > 0 && x.status_history[0].notes) {
            x.notes = x.status_history[0].notes
          }
          return x
        })))
        if (this.selectedRow && this.selectedRow.index) {
          newValue[this.selectedRow.index].isEdit = this.items[this.selectedRow.index].isEdit = true
        }
      },
      deep: true
    }
  },
  mixins: [AddressMixins],
  mounted() {
    this.items = JSON.parse(JSON.stringify(this.fleetRentalOrderVehicles.map(x => {
      if (x.status_history && x.status_history.length > 0 && x.status_history[0].notes) {
        x.notes = x.status_history[0].notes
      }
      return x
    })))
    this.items = this.items.map(x => {
      if (!x.meta) {
        x.meta = {
          serviceDetail: {
            items: [],
            quantity: 0
          }
        }
      }
      if (!x.meta.serviceDetail) {
        x.meta.serviceDetail = {
          items: [],
          quantity: 0
        }
      }
      return x
    })
  },
  methods: {
    setMinMaxServices(value) {
      return this.alreadyAssigned > this.fleetRentalOrder.meta.serviceDefinition.quantity ? this.fleetRentalOrder.meta.serviceDefinition.quantity : (value >= 0 ? value : 0)
    },
    editRowHandler(row) {
      this.items[row.index].isEdit = !this.items[row.index].isEdit
      this.editing = !!this.items[row.index].isEdit
    },
    cancelEdit(row) {
      this.editRowHandler(row)
      this.$parent.$parent.getItem()
    },
    cleanupRowData(index) {
      if (confirm(this.$gettext('Are you sure you want to DELETE this vehicle data?'))) {
        Object.assign(this.items[index], {...this.items[index], ...this.item})
        this.saveFleetRentalOrderVehicle({item: this.items[index]})
      }
    },
    setFleetRentalOrderCourier(item, index) {
      this.items[index].courier = item
      this.items[index].courier_id = item.id
    },
    deleteFleetRentalOrderCourier(index) {
      this.items[index].courier = null
      this.items[index].courier_id = null
    },
    setFleetRentalOrderVehicle(item, index) {
      this.items[index].vehicle = item
      this.items[index].vehicle_id = item.id
    },
    deleteFleetRentalOrderVehicle(index) {
      this.items[index].vehicle = null
      this.items[index].vehicle_id = null
    },
    async setFleetRentalOrderVehiclePlaceId(item, index) {
      await this.checkPlace(item)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.items[index].place = this.theCheckedAddress
          this.items[index].place_id = this.theCheckedAddress.id
        }
        this.theCheckedAddress = {}
      })
    },
    deleteFleetRentalOrderVehiclePlaceId(index) {
      this.items[index].place = {}
      this.items[index].place_id = null
    },
    async saveFleetRentalOrderVehicle(row) {
      if (row.item.id) {
        if (row.item.status === 'incidence' && (!row.item.notes && !row.item.status_history[0].notes)) {
          alert('Add notes')
          this.items[row.index].isEdit = true
          return false
        }
        // delete row.item.courier
        // delete row.item.vehicle
        // delete row.item.place
        // delete row.item.status_history
        await this.$http.patch(this.$appConfig.api.baseUrl + 'fleet-rental-order-vehicles/' + row.item.id, row.item)
          .then(response => {
            this.items[row.index] = this.fleetRentalOrderVehicles[row.index] = {...response, isEdit: false}
          })
          .finally(() => {
            this.$parent.$parent.getItem()
          })
      } else {
        await this.$http.post(this.$appConfig.api.baseUrl + 'fleet-rental-order-vehicles', row.item)
          .then(response => {
            this.items[row.index] = this.fleetRentalOrderVehicles[row.index] = {...response, isEdit: false}
          })
          .finally(() => {
            this.$parent.$parent.getItem()
          })
      }
    },
    async deleteRow(row) {
      if (row.item.id) {
        if (row.item.status !== 'done') {
          if (confirm(this.$gettext('Are you sure you want to COMPLETELY DELETE this vehicle from the Order?\n\nWARNING: THIS ACTION CANNOT BE UNDONE!'))) {
            await this.$http.delete(this.$appConfig.api.baseUrl + 'fleet-rental-order-vehicles/' + row.item.id)
              .then(response => {
                this.$parent.fleetRentalOrder.amount_requested--
                this.$parent.saveOrder()
              })
          }
        }
      } else {
        this.$parent.fleetRentalOrder.amount_requested--
        this.$parent.saveOrder()
      }
    },
    addServiceDetailItem() {
      this.items[this.selectedRow.index].meta.serviceDetail.items.push(this.serviceDetail.code)
      this.items[this.selectedRow.index].meta.serviceDetail.quantity++
      this.$nextTick(() => {
        this.items[this.selectedRow.index].isEdit = true
      })
      this.serviceDetail = {
        code: ''
      }
    },
    deleteServiceDetailItem(index) {
      this.items[this.selectedRow.index].meta.serviceDetail.items.splice(index, 1)
      this.items[this.selectedRow.index].meta.serviceDetail.quantity--
      this.$nextTick(() => {
        this.items[this.selectedRow.index].isEdit = true
      })
    },
    openModalAccountingNote(item) {
      this.$root.$emit('bv::show::modal', 'modalMenuAccountingNote')
      this.$root.$once('bv::modal::shown', (e) => {
        if (e.componentId === 'modalMenuAccountingNote') {
          setTimeout(() => {
            this.$root.$children[0].$refs.accountingNoteForm.accountSearchType = item.courier.company ? 'company' : 'user'
            setTimeout(() => {
              this.$root.$children[0].$refs.accountingNoteForm.accountingNote = {
                account_id: item.courier.company ? item.courier.company.account.id : item.courier.account.id,
                type: 'credit',
                amount: null,
                currency: item.currency,
                shipping_id: null,
                service_id: null,
                fleet_rental_order_id: item.fleet_rental_order_id,
                relationship: 'provider',
                reference: '',
                visibility: 'all',
                imputation_date: null,
                status: 'pending'
              }
              this.$root.$children[0].$refs.accountingNoteForm.accountOwner = item.courier.company ? item.courier.company.name : item.courier.name
            }, 100)
          }, 500)
        }
      })
    }
  }
}
</script>
